import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import SpinnerBlock from 'components/others/SpinnerBlock';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TeamReportRowType } from '@econtractor/types';
import * as apiReport from 'api/apiReports';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import ESpinner from 'components/others/Spinner';
import type { ReportDateFilterState } from '../helper/reportObjectsStorage';
import {
    loadReportTeamsFilters,
    saveReportTeamsFilters,
} from '../helper/reportTeamsStorage';
import ReportDate from '../ReportDate';
import ReportExportButton from '../ReportExportButton';
import ReportTeamTable from './ReportTeamsTable';
import { exportTeamsReport } from './model/exportTeamsReport';

const ReportTeam = () => {
    const { t } = useTranslation(['Reports', 'common']);
    const { showToast } = useToast();
    const toastRef = useRef(showToast);
    toastRef.current = showToast;
    const { localSettings } = useSettings();
    const savedFilters = useMemo(() => loadReportTeamsFilters(), []);
    const [exporting, setExporting] = useState(false);

    const [teamsList, setTeamsList] = useState<TeamReportRowType[] | null>(null);
    const [isReporting, setIsReporting] = useState(false);
    const [dates, setDates] = useState<[number, number] | null>(savedFilters.dates);
    const [dateFilter, setDateFilter] = useState<ReportDateFilterState>({
        period: savedFilters.period,
        dates: savedFilters.dates,
        customRange: savedFilters.customRange,
    });

    const persistFilters = useCallback(
        (filter: ReportDateFilterState) => {
            saveReportTeamsFilters(filter);
        },
        [],
    );

    const handleDateFilterChange = (filter: ReportDateFilterState) => {
        setDateFilter(filter);
        setDates(filter.dates);
        persistFilters(filter);
    };

    const fromDate = dates?.[0];
    const toDate = dates?.[1];
    const fetchReport = useCallback(async () => {
        if (!fromDate || !toDate) return;
        setIsReporting(true);
        const resRep = await apiReport.get4Teams(fromDate, toDate);
        if (!resRep) toastRef.current(t('common:messError'), 'danger');
        setTeamsList(resRep?.teams ?? null);
        setIsReporting(false);
    }, [fromDate, toDate, t]);

    useEffect(() => {
        void fetchReport();
    }, [fetchReport]);

    const handleExport = async () => {
        if (!teamsList || exporting) return;
        setExporting(true);
        try {
            const exported = await exportTeamsReport({
                teams: teamsList,
                teamNames: localSettings?.DIRS?.Team || [],
                dates,
                period: dateFilter.period,
                rate: Number(localSettings?.RATE?.current ?? 1) || 1,
                t,
            });
            if (!exported) showToast(t('export.empty'), 'warning');
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Row>
            <Col className="col-sticky" sm={12}>
                <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start gap-2">
                    <div className="flex-shrink-0">
                        <h4 className="p-0 m-0 mb-0 me-2 text-nowrap">{t('common:SideBarUl.itemRepostsTeams')}</h4>
                    </div>
                    <div className="mt-2 mt-sm-0 mb-2 page-toolbar-actions">
                        <ReportExportButton
                            disabled={!teamsList || isReporting || exporting}
                            onClick={() => {
                                void handleExport();
                            }}
                        />
                        <ReportDate
                            setDates={setDates}
                            disabled={isReporting}
                            initialFilter={dateFilter}
                            onFilterChange={handleDateFilterChange}
                        />
                        <ListToolBarActions onRefresh={() => void fetchReport()} disabled={isReporting} />
                    </div>
                </div>
            </Col>

            <Col sm={12}>
                <div className="px-0 position-relative">
                    <SpinnerBlock onlyBlur={true} show={isReporting && teamsList !== null} />
                        {teamsList ? <ReportTeamTable teamList={teamsList} /> : <ESpinner />}
                    
                </div>
            </Col>
        </Row>
    );
};

export default ReportTeam;
